/**
 * Enroll customer page.
 *
 * The ONLY part of the enroll feature that knows about:
 *   - the admin role guard
 *   - redux toast dispatch
 *   - the bb-core-ui app shell
 *
 * Everything else (form, brand-preview, progress, success, partial panels,
 * the use-enrollment hook) is self-contained and prop-driven, so this page
 * can be cloned for the future public/anonymous variant by swapping in a
 * different api caller and skipping the role guard.
 */

import { useCallback, useRef, useState } from "react"
import { Rocket } from "lucide-react"
import { PageHeader } from "../../common/ui"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants/navbar"
import { useAppDispatch } from "../../common/hooks"
import { pushToast } from "../../redux/slices/notifications"
import { useEnrollment } from "./use-enrollment"
import EnrollForm, {
  EnrollFormHandle,
  EnrollFormValues,
  buildEnrollPayload,
  initialEnrollValues
} from "./components/enroll-form"
import BrandPreview from "./components/brand-preview"
import EnrollmentProgress from "./components/enrollment-progress"
import EnrollmentSuccess from "./components/enrollment-success"
import EnrollmentPartial, {
  EnrollmentRetryBlocked
} from "./components/enrollment-partial"
import EnrollmentSummary from "./components/enrollment-summary"
import { mapFieldErrorsToFormik } from "./use-enrollment"

const Enroll = () => {
  const dispatch = useAppDispatch()
  const formRef = useRef<EnrollFormHandle | null>(null)
  const [liveValues, setLiveValues] =
    useState<EnrollFormValues>(initialEnrollValues)

  const handleIdempotencyConflict = useCallback(
    (message: string) => {
      dispatch(
        pushToast({
          msg:
            message || "Something changed between attempts. Please re-submit.",
          theme: "danger",
          timeout: 6000
        })
      )
    },
    [dispatch]
  )

  const handleError = useCallback(
    (message: string) => {
      dispatch(
        pushToast({
          msg: message || "Enrollment failed.",
          theme: "danger",
          timeout: 6000
        })
      )
    },
    [dispatch]
  )

  const { state, start, retry, reset } = useEnrollment({
    onIdempotencyConflict: handleIdempotencyConflict,
    onError: handleError
  })

  const handleSubmit = useCallback(
    async (values: EnrollFormValues) => {
      const payload = buildEnrollPayload(values)
      const next = await start(payload)
      // Surface server-side validation errors onto the form fields
      if (next.kind === "validation_error") {
        formRef.current?.applyServerErrors(
          mapFieldErrorsToFormik(next.fields) as any
        )
        dispatch(
          pushToast({
            msg: next.message,
            theme: "danger",
            timeout: 6000
          })
        )
      }
    },
    [start, dispatch]
  )

  const handleEditAndResubmit = useCallback(() => {
    reset()
  }, [reset])

  const formLocked =
    state.kind === "submitting" ||
    state.kind === "polling" ||
    state.kind === "complete" ||
    state.kind === "partial" ||
    state.kind === "retry_blocked"

  const submitLabel = (() => {
    if (state.kind === "submitting") return "Starting…"
    if (state.kind === "polling") return "Enrolling…"
    if (state.kind === "complete") return "Enrolled"
    if (state.kind === "partial") return "Paused"
    return "Enroll customer"
  })()

  // Right-panel router
  const renderRightPanel = () => {
    if (state.kind === "complete") {
      return (
        <EnrollmentSuccess
          enrollment={state.enrollment}
          companyName={liveValues.company_name}
          onEnrollAnother={() => {
            reset()
          }}
        />
      )
    }
    if (state.kind === "partial") {
      return (
        <EnrollmentPartial
          enrollment={state.enrollment}
          onRetry={retry}
          onEditAndResubmit={handleEditAndResubmit}
        />
      )
    }
    if (state.kind === "retry_blocked") {
      return (
        <EnrollmentRetryBlocked
          enrollmentId={state.enrollment_id}
          message={state.message}
        />
      )
    }
    if (state.kind === "polling") {
      return <EnrollmentProgress enrollment={state.enrollment} isLive={true} />
    }
    if (state.kind === "submitting") {
      // Tiny "starting" placeholder; the polling state replaces this almost immediately
      return (
        <div className="overflow-hidden rounded-2xl border border-primary-200 bg-white shadow-sm px-6 py-8 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <Rocket size={16} className="animate-pulse" />
            <span className="font-mono uppercase tracking-wider">
              Igniting enrollment…
            </span>
          </div>
        </div>
      )
    }
    // idle, validation_error, idempotency_conflict, error → preview
    return <BrandPreview values={liveValues} />
  }

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.enroll}>
      <div className="min-h-screen bg-neutral-50/40">
        <div className="max-w-6xl mx-auto p-6">
          <PageHeader
            title={
              <span className="flex items-center gap-3">
                <span>Enroll customer</span>
                <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-primary-600">
                  Admin
                </span>
              </span>
            }
            hasSubtitle
            subtitle="Spin up a complete customer — org, brand, first LO, rateflow, profiles, workflow, and embeds — in one shot."
            className="mb-6"
          />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
            {/* LEFT — Form or read-only summary */}
            <div>
              {state.kind === "complete" ? (
                <EnrollmentSummary
                  values={liveValues}
                  enrollment={state.enrollment}
                />
              ) : (
                <EnrollForm
                  ref={formRef}
                  locked={formLocked}
                  submitLabel={submitLabel}
                  onSubmit={handleSubmit}
                  onValuesChange={setLiveValues}
                />
              )}
            </div>

            {/* RIGHT — Sticky preview / progress / terminal */}
            <aside className="lg:sticky lg:top-6">{renderRightPanel()}</aside>
          </div>
        </div>
      </div>
    </RequireRole>
  )
}

export default Enroll
